<template>
    <date-picker
        :class="{ 'range-date': selectionMode === 'range', 'full-width': props.fullWidth }"
        :model-value="displayDate"
        :selection-mode="props.selectionMode"
        :date-format="props.format"
        :view="props.view"
        :disabled="props.disabled"
        :invalid="props.invalid"
        :manual-input="props.manualInput"
        select-other-months
        @update:model-value="updateDate"
        :min-date="props.minDate"
        :max-date="props.maxDate"
        :show-time="props.showTime"
    />
</template>

<script lang="ts" setup>
import dayjs from 'dayjs';
import DatePicker from 'primevue/datepicker';
import { ref, watch } from 'vue';

const props = withDefaults(defineProps<{
    modelValue: Date | [Date, Date] | Array<Date | null> | null;
    selectionMode?: 'single' | 'multiple' | 'range';
    disabled?: boolean;
    format?: string;
    manualInput?: boolean;
    minDate?: Date;
    maxDate?: Date;
    invalid?: boolean;
    view?: 'date' | 'month' | 'year';
    fullWidth?: boolean;
    showTime?: boolean;
}>(), {
    format: 'dd/mm/yy',
    manualInput: true,
    minDate: undefined,
    maxDate: undefined,
    invalid: false,
    view: 'date',
    selectionMode: 'single',
    fullWidth: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: [Date, Date] | Date];
}>();

type DatePickerReturnObject = Date | Date[] | (Date | null)[] | null | undefined;

const displayDate = ref<DatePickerReturnObject>(null);

function isDateArray (value: DatePickerReturnObject): value is [Date, Date] {
    return Array.isArray(value) && value.length === 2 && value.every((v) => v instanceof Date);
}

function updateDate (value: DatePickerReturnObject): void {
    if (props.selectionMode === 'single') {
        updateSingleDate(value);
        return;
    }
    updateRangeDate(value);
}

function updateSingleDate (value: DatePickerReturnObject):void {
    if (Array.isArray(value) || !value) return;
    emit('update:modelValue', value);
}

function updateRangeDate (value: DatePickerReturnObject): void {
    if (!value || !Array.isArray(value) || value.length !== 2) return;
    if (value[0] !== null && value[1] === null) {
        emit('update:modelValue', [value[0], value[0]]);
        return;
    }
    if (!isDateArray(value)) return;
    emit('update:modelValue', value);
}

watch(() => props.modelValue, newVal => {
    if (isDateArray(newVal) && dayjs(newVal[0]).isSame(dayjs(newVal[1]))) {
        displayDate.value = [newVal[0], null];
        return;
    }
    displayDate.value = newVal;
}, { immediate: true });

</script>
<style lang="scss" scoped>
.range-date {
    :deep(.p-datepicker-input) {
        width: 23rem;
    }
}

.full-width {
    width: 100%;

    :deep(.p-datepicker-input) {
        width: 100%;
    }
}
</style>
